import { EyeOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { SpaceApi } from "../../SpaceApi";
import type { HomeCourseItem } from "../../SpaceType";
import { formatLearnCount } from "../../spacePageUtils";

/** 教学视频 */
export function SpaceHomeCoursePreview(props: {
  spaceId: string;
  courses: HomeCourseItem[];
}): JSX.Element {
  const { spaceId, courses } = props;
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [learnCounts, setLearnCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const next: Record<string, number> = {};
    courses.forEach((c, i) => {
      const key = c.tutorialId?.trim() || `${c.title}-${i}`;
      next[key] = c.learnerCount ?? 0;
    });
    setLearnCounts(next);
  }, [courses]);

  const onCloseVideo = useCallback(() => {
    setVideoUrl(null);
    setVideoTitle("");
  }, []);

  const onPlay = useCallback(
    (course: HomeCourseItem, index: number) => {
      const url = course.videoUrl?.trim();
      if (!url) {
        return;
      }
      const key = course.tutorialId?.trim() || `${course.title}-${index}`;
      setLearnCounts((prev) => ({
        ...prev,
        [key]: (prev[key] ?? course.learnerCount ?? 0) + 1,
      }));
      const tutorialId = course.tutorialId?.trim();
      if (tutorialId) {
        void SpaceApi.recordVideoTutorialPlay(spaceId, tutorialId).then((count) => {
          if (count != null) {
            setLearnCounts((prev) => ({ ...prev, [key]: count }));
          }
        });
      }
      setVideoUrl(url);
      setVideoTitle(course.title);
    },
    [spaceId]
  );

  if (courses.length === 0) {
    return <></>;
  }

  return (
    <>
      <section className="space-home-feed__section" aria-label="空间教程">
        <p className="space-home-feed__section-hint">空间教程</p>
        <div className="space-home-feed__grid" role="list">
          {courses.map((c, i) => {
            const hasVideo = c.videoUrl != null && c.videoUrl.trim() !== "";
            const key = c.tutorialId?.trim() || `${c.title}-${i}`;
            const count = learnCounts[key] ?? c.learnerCount ?? 0;
            return (
              <button
                key={key}
                type="button"
                className={
                  hasVideo
                    ? "space-home-feed__tile"
                    : "space-home-feed__tile space-home-feed__tile--static"
                }
                role="listitem"
                onClick={() => {
                  if (hasVideo) {
                    onPlay(c, i);
                  }
                }}
              >
                <div className="space-home-feed__tile-thumb">
                  <img src={c.coverUrl} alt="" loading="lazy" />
                  {hasVideo ? (
                    <span className="space-home-feed__play-icon" aria-hidden />
                  ) : null}
                  <div className="space-home-feed__tile-bottom-bar">
                    <span className="space-home-feed__tile-bottom-title">{c.title}</span>
                    <span className="space-home-feed__tile-view-stat" aria-label={`${count} 次学习`}>
                      <EyeOutlined className="space-home-feed__tile-view-icon" aria-hidden />
                      {formatLearnCount(count)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <Modal
        open={videoUrl != null && videoUrl !== ""}
        onCancel={onCloseVideo}
        footer={null}
        width={Math.min(960, typeof window !== "undefined" ? window.innerWidth - 32 : 960)}
        className="space-home-feed__video-modal"
        title={videoTitle}
        destroyOnClose
        centered
      >
        {videoUrl ? (
          <video
            className="space-home-feed__video"
            src={videoUrl}
            controls
            playsInline
            preload="metadata"
          />
        ) : null}
      </Modal>
    </>
  );
}
